import React, { useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  ButtonGroup,
  Box,
  Collapse,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { updateAppointmentStatus } from '../../services/api';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

interface Appointment {
  id: number;
  client_name: string;
  service_name: string;
  date: string;  // Date string from backend
  time: string;  // Time string from backend
  status: string;
  duration: number;
}

interface AppointmentListProps {
  appointments: Appointment[];
  onStatusUpdate: (appointmentId: number, status: AppointmentStatus) => void;
}

type AppointmentStatus = 'completed' | 'cancelled';

const AppointmentList: React.FC<AppointmentListProps> = ({
  appointments,
  onStatusUpdate,
}) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleExpand = (appointmentId: number) => {
    setExpandedId((prev) => (prev === appointmentId ? null : appointmentId)); // Toggle expand/collapse
  };

  const handleStatusUpdate = async (appointmentId: number, status: AppointmentStatus, e:React.MouseEvent) => {
    e.preventDefault();
    try {
      await updateAppointmentStatus(appointmentId, status);
      onStatusUpdate(appointmentId, status);
    } catch (error) {
      console.error('Error updating appointment status:', error);
    }
  };

  
  if (isMobile) {
    // Group appointments by date
    const groupedAppointments: { [key: string]: Appointment[] } = appointments.reduce((groups, appointment) => {
      const dateKey = format(parseISO(appointment.date), 'MMM dd, yyyy');
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(appointment);
      return groups;
    }, {} as { [key: string]: Appointment[] });
  
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {Object.keys(groupedAppointments).map((dateKey) => (
          <Paper key={dateKey} className="schedule-item" sx={{ padding: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {dateKey} {/* Show the formatted date */}
            </Typography>
            {groupedAppointments[dateKey].map((appointment) => (
              <Box key={appointment.id} sx={{ mb: 2 }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  onClick={() => handleExpand(appointment.id)} // Handle the click to expand
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {appointment.time}
                    </Typography>
                  </Box>
                  {expandedId === appointment.id ? (
                    <KeyboardArrowUp /> // Show up arrow if expanded
                  ) : (
                    <KeyboardArrowDown /> // Show down arrow if collapsed
                  )}
                </Box>
  
                <Collapse in={expandedId === appointment.id}>
                  <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="body1" gutterBottom>
                      Client: {appointment.client_name}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      Service: {appointment.service_name}
                    </Typography>
                    <Chip 
                      label={appointment.status}
                      color={
                        appointment.status === 'completed' ? 'success' :
                        appointment.status === 'cancelled' ? 'error' : 'default'
                      }
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    {appointment.status === 'scheduled' && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                          type="button"
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={(e) => {
                            // e.stopPropagation();
                            onStatusUpdate(appointment.id, 'completed');
                          }}
                        >
                          Complete
                        </Button>
                        <Button
                          type="button"
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={(e) => {
                            // e.stopPropagation();
                            onStatusUpdate(appointment.id, 'cancelled');
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>
            ))}
          </Paper>
        ))}
      </Box>
    );
  }
  
  

  return (
    <TableContainer 
      component={Paper}
      sx={{ 
        background: (theme) => theme.palette.background.paper,
        backdropFilter: 'blur(20px)',
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: 'text.primary', fontWeight: 500 }}>Date</TableCell>
            <TableCell sx={{ color: 'text.primary', fontWeight: 500 }}>Time</TableCell>
            <TableCell sx={{ color: 'text.primary', fontWeight: 500 }}>Client</TableCell>
            <TableCell sx={{ color: 'text.primary', fontWeight: 500 }}>Service</TableCell>
            <TableCell sx={{ color: 'text.primary', fontWeight: 500 }}>Status</TableCell>
            <TableCell sx={{ color: 'text.primary', fontWeight: 500 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {appointments.map((appointment) => {
            // Parse the date string
            const appointmentDate = parseISO(appointment.date);
            
            return (
              <TableRow key={appointment.id}>
                <TableCell sx={{ color: 'text.primary' }}>
                  {format(appointmentDate, 'MMM dd, yyyy')}
                </TableCell>
                <TableCell sx={{ color: 'text.primary' }}>
                  {appointment.time}
                </TableCell>
                <TableCell sx={{ color: 'text.primary' }}>
                  {appointment.client_name}
                </TableCell>
                <TableCell sx={{ color: 'text.primary' }}>
                  {appointment.service_name}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={appointment.status}
                    sx={{
                      backgroundColor: getStatusColor(appointment.status),
                      color: 'text.light',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <ButtonGroup 
                    size="small"
                    sx={{
                      '& .MuiButton-root': {
                        color: 'text.primary',
                        borderColor: 'divider',
                        '&:hover': {
                          backgroundColor: 'primary.light',
                        },
                      },
                    }}
                  >
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusUpdate(appointment.id, 'cancelled', e);
                      }}
                      disabled={['cancelled', 'completed'].includes(appointment.status)}
                      sx={{
                        '&.Mui-disabled': {
                          color: 'text.disabled',
                        },
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusUpdate(appointment.id, 'completed', e);
                      }}
                      disabled={appointment.status === 'completed' || appointment.status === 'cancelled'}
                      sx={{
                        '&.Mui-disabled': {
                          color: 'text.disabled',
                        },
                      }}
                    >
                      Complete
                    </Button>
                  </ButtonGroup>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'cancelled':
      return '#F44336';  // Red
    case 'completed':
      return '#2196F3';  // Blue
    default:
      return '#757575';  // Grey
  }
};

export default AppointmentList; 